import { useState } from "react";
import { motion } from "framer-motion";
import { Download, Mail, Building2, MapPin, FileSpreadsheet, CheckCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { supabase } from "@/integrations/supabase/client";
import { generatePEOComparisonSpreadsheet } from "@/lib/spreadsheet-generator";
import { useToast } from "@/hooks/use-toast";

import { analyzeWorkEmail } from "@/utils/companyEmailAnalysis";

const formSchema = z.object({
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"), 
  email: z.string().email("Please enter a valid email address").refine((email) => {
    const emailInfo = analyzeWorkEmail(email);
    return emailInfo?.isWorkEmail === true;
  }, "Please use a company email address (no Gmail, Yahoo, Hotmail, etc.)"),
  companySize: z.string().min(1, "Company size is required"),
  state: z.string().min(1, "State is required"),
});

type FormData = z.infer<typeof formSchema>;

const companySizes = [
  "1-5 employees",
  "6-10 employees", 
  "11-25 employees",
  "26-50 employees",
  "51-100 employees",
  "100+ employees"
];

const states = [
  "Alabama", "Alaska", "Arizona", "Arkansas", "California", "Colorado", "Connecticut",
  "Delaware", "Florida", "Georgia", "Hawaii", "Idaho", "Illinois", "Indiana", "Iowa",
  "Kansas", "Kentucky", "Louisiana", "Maine", "Maryland", "Massachusetts", "Michigan",
  "Minnesota", "Mississippi", "Missouri", "Montana", "Nebraska", "Nevada", "New Hampshire",
  "New Jersey", "New Mexico", "New York", "North Carolina", "North Dakota", "Ohio",
  "Oklahoma", "Oregon", "Pennsylvania", "Rhode Island", "South Carolina", "South Dakota",
  "Tennessee", "Texas", "Utah", "Vermont", "Virginia", "Washington", "West Virginia",
  "Wisconsin", "Wyoming"
];

export const PEOComparisonLeadMagnet = () => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isDownloaded, setIsDownloaded] = useState(false);
  const { toast } = useToast();

  const form = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      firstName: "",
      lastName: "", 
      email: "",
      companySize: "",
      state: "",
    },
  });

  const onSubmit = async (data: FormData) => {
    setIsSubmitting(true);
    
    try {
      // Capture lead in Supabase
      const { error } = await supabase
        .from("lead_captures")
        .insert({
          first_name: data.firstName,
          last_name: data.lastName,
          email: data.email,
          company_size: data.companySize,
          state: data.state,
          resource_downloaded: "peo_comparison_spreadsheet",
        });

      if (error) {
        throw error;
      }

      // Generate and download spreadsheet
      generatePEOComparisonSpreadsheet();
      
      setIsDownloaded(true);
      toast({
        title: "Download Started!",
        description: "Your PEO comparison spreadsheet is downloading. Check your downloads folder.",
      });

      // Reset form
      form.reset();
    } catch (error) {
      console.error("Error:", error);
      toast({
        title: "Download Failed",
        description: "There was an error generating your spreadsheet. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-16 bg-gradient-to-br from-primary/5 via-background to-primary/10">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          viewport={{ once: true }}
          className="max-w-4xl mx-auto"
        >
          <div className="bg-background rounded-xl shadow-lg overflow-hidden">
            <div className="grid md:grid-cols-2 gap-0">
              {/* Left side - Content */}
              <div className="p-8 bg-gradient-to-br from-primary/10 to-primary/5">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-12 h-12 bg-primary/20 rounded-full flex items-center justify-center">
                    <FileSpreadsheet className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-foreground">Free PEO Comparison Tool</h3>
                    <p className="text-sm text-muted-foreground">Expert-designed evaluation template</p>
                  </div>
                </div>

                <p className="text-muted-foreground mb-6 leading-relaxed">
                  Download our comprehensive Excel template to systematically compare PEO quotes. This tool helps you evaluate pricing, services, and benefits side-by-side to make the best decision for your organization.
                </p>

                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-sm">Compare up to 5 PEO providers at once</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-sm">Pre-built formulas for cost analysis</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-sm">Service & benefit comparison matrix</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-primary flex-shrink-0" />
                    <span className="text-sm">Need help? The PEO App team will assist for free</span>
                  </div>
                </div>
              </div>

              {/* Right side - Form */}
              <div className="p-8">
                <Form {...form}>
                  <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <FormField
                        control={form.control}
                        name="firstName"
                        render={({ field }) => (
                          <FormItem>
                            <Label htmlFor="firstName">First Name *</Label>
                            <FormControl>
                              <Input
                                id="firstName"
                                placeholder="Jane"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />

                      <FormField
                        control={form.control}
                        name="lastName"
                        render={({ field }) => (
                          <FormItem>
                            <Label htmlFor="lastName">Last Name *</Label>
                            <FormControl>
                              <Input
                                id="lastName"
                                placeholder="Doe"
                                {...field}
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>

                    <FormField
                      control={form.control}
                      name="email"
                      render={({ field }) => (
                        <FormItem>
                          <Label htmlFor="email" className="flex items-center gap-2">
                            <Mail className="w-4 h-4" />
                            Company Email Address *
                          </Label>
                          <FormControl>
                            <Input
                              id="email"
                              placeholder="your@company.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="companySize"
                      render={({ field }) => (
                        <FormItem>
                          <Label className="flex items-center gap-2">
                            <Building2 className="w-4 h-4" />
                            Company Size *
                          </Label>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select company size" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {companySizes.map((size) => (
                                <SelectItem key={size} value={size}>
                                  {size}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={form.control}
                      name="state"
                      render={({ field }) => (
                        <FormItem>
                          <Label className="flex items-center gap-2">
                            <MapPin className="w-4 h-4" />
                            State *
                          </Label>
                          <Select onValueChange={field.onChange} value={field.value}>
                            <FormControl>
                              <SelectTrigger>
                                <SelectValue placeholder="Select state" />
                              </SelectTrigger>
                            </FormControl>
                            <SelectContent>
                              {states.map((state) => (
                                <SelectItem key={state} value={state}>
                                  {state}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full mt-6"
                      disabled={isSubmitting}
                      size="lg"
                    >
                      <Download className="w-4 h-4 mr-2" />
                      {isSubmitting ? "Generating..." : "Download Free Template"}
                    </Button>

                    {isDownloaded && (
                      <motion.div
                        initial={{ opacity: 0, scale: 0.95 }}
                        animate={{ opacity: 1, scale: 1 }}
                        className="bg-primary/10 text-primary text-sm text-center py-2 px-3 rounded-md"
                      >
                        ✓ Download started! Check your downloads folder.
                      </motion.div>
                    )}
                  </form>
                </Form>

                <p className="text-xs text-muted-foreground mt-4 text-center">
                  We'll never spam you. Unsubscribe at any time.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};